import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Chip, Button, Stack, Divider } from '@mui/material';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import { getAdminOrderAcerStatus, AcerStatusResponse } from '../../services/admin-service';

const formatDateFromYYYYMMDD = (value?: string) => {
  if (!value || value.trim() === '' || value.trim() === '        ') return '';
  if (value.includes('/')) return value; // already formatted like dd/mm/yyyy hh:mm
  if (value.length !== 8) return value;
  const y = value.substring(0, 4);
  const m = value.substring(4, 6);
  const d = value.substring(6, 8);
  return `${d}/${m}/${y}`;
};

const decodeBase64ToBlobUrl = (b64?: string) => {
  if (!b64) return null;
  try {
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/xml' });
    return URL.createObjectURL(blob);
  } catch (e) {
    return null;
  }
};

const AdminOrderAcerStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<AcerStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Pedidos', path: '/admin/orders' },
      { label: 'Status Acer' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const resp = await getAdminOrderAcerStatus(id);
        if (!resp) throw new Error('Sem dados');
        setData(resp);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Falha ao consultar status na Acer', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, enqueueSnackbar]);

  const xmlUrl = useMemo(() => decodeBase64ToBlobUrl(data?.retorno?.xml), [data]);

  const handleDownloadXml = (b64?: string, fileName?: string) => {
    const url = decodeBase64ToBlobUrl(b64);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || 'pedido'}.xml`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Status do Pedido na Acer</Typography>
        <Button component={RouterLink} to="/admin/orders" variant="outlined" size="small">
          Voltar
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Typography>Carregando...</Typography>
        ) : !data ? (
          <Typography>Sem dados</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography variant="body1">Código:</Typography>
              <Chip label={data?.retorno?.id || '-'} />
              <Chip
                label={data?.retorno?.statusDescricao || data?.mensagem || '-'}
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* New structure: list of NFs in retorno.datanf */}
            {Array.isArray(data?.retorno?.datanf) && data?.retorno?.datanf?.length ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                  Notas Fiscais
                </Typography>
                <Stack spacing={2}>
                  {data!.retorno!.datanf!.map((nf, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          Número Pedido Cliente: {data?.retorno?.numeroPedidoCli || '-'}
                        </Typography>
                        <Typography variant="body2">
                          Status Código: {nf.statusCodigo || '-'}
                        </Typography>
                        <Typography variant="body2">
                          Status Descrição:{' '}
                          {nf.statusDescricao || data?.retorno?.statusDescricao || '-'}
                        </Typography>
                        <Typography variant="body2">Número Nota: {nf.numeroNota || '-'}</Typography>
                        <Typography variant="body2">
                          Data Faturamento: {formatDateFromYYYYMMDD(nf.dataFaturamento)}
                        </Typography>
                        <Typography variant="body2">
                          Data Envio SFC: {nf.dataEnvioSFC || '-'}
                        </Typography>
                        <Typography variant="body2">
                          Data Expedição: {formatDateFromYYYYMMDD(nf.dataExpedicao)}
                        </Typography>
                        <Typography variant="body2">
                          Data Entrega: {formatDateFromYYYYMMDD(nf.dataEntrega)}
                        </Typography>
                        <Typography variant="body2">
                          Código de Rastreamento: {nf.codigoRastreio || '-'}
                        </Typography>
                        {nf.xml && (
                          <Box mt={1}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() =>
                                handleDownloadXml(
                                  nf.xml,
                                  `${data?.retorno?.id || 'pedido'}-${nf.numeroNota || idx + 1}`
                                )
                              }
                            >
                              Baixar XML
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ) : (
              <>
                {/* Fallback: old flat structure */}
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Número Pedido Cliente: {data?.retorno?.numeroPedidoCli || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Status Código: {data?.retorno?.statusCodigo || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Número Nota: {data?.retorno?.numeroNota || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Data Faturamento: {formatDateFromYYYYMMDD(data?.retorno?.dataFaturamento)}
                  </Typography>
                  <Typography variant="body2">
                    Data Envio SFC: {data?.retorno?.dataEnvioSFC || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Data Expedição: {formatDateFromYYYYMMDD(data?.retorno?.dataExpedicao)}
                  </Typography>
                  <Typography variant="body2">
                    Data Entrega: {formatDateFromYYYYMMDD(data?.retorno?.dataEntrega)}
                  </Typography>
                  <Typography variant="body2">
                    Código de Rastreamento: {data?.retorno?.codigoRastreio || '-'}
                  </Typography>
                </Stack>

                {xmlUrl && (
                  <Box mt={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleDownloadXml(data?.retorno?.xml, data?.retorno?.id)}
                    >
                      Baixar XML
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AdminOrderAcerStatusPage;
